// Cloudflare Turnstile — invisible bot check for the lead forms.
// Rendered with `appearance: "interaction-only"`, so nothing shows on the page
// unless Cloudflare actually needs the visitor to interact. The script is only
// loaded when a form is submitted, so it costs nothing on page load.
//
// Fail-open by design: if the script is blocked or slow, we return an empty
// token and the server still accepts the lead. Never lose a real customer.

export const TURNSTILE_SITE_KEY = "0x4AAAAAAEzbPk9ivG8vaTYO";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  execute: (id: string, opts?: Record<string, unknown>) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi | null> | null = null;

function loadScript(): Promise<TurnstileApi | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi | null>((resolve) => {
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve(window.turnstile ?? null);
    el.onerror = () => resolve(null);
    document.head.appendChild(el);
  });
  return scriptPromise;
}

function container(): HTMLElement {
  let host = document.getElementById("cp-turnstile-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "cp-turnstile-host";
    host.style.cssText =
      "position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:2147483647;";
    document.body.appendChild(host);
  }
  return host;
}

/**
 * Warms the Turnstile script ahead of submit (called when a lead form mounts)
 * so the bot check adds no perceptible delay when the visitor hits submit.
 */
export function preloadTurnstile(): void {
  if (typeof window === "undefined") return;
  void loadScript();
}

/**
 * Returns a Turnstile token, or "" when the check could not run.
 * `timeoutMs` is a total deadline covering script load + challenge, so a slow
 * or blocked Cloudflare never holds a lead hostage.
 */
export async function getTurnstileToken(timeoutMs = 3500): Promise<string> {
  const deadline = Date.now() + timeoutMs;
  try {
    const api = await Promise.race([
      loadScript(),
      new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
    ]);
    if (!api) return "";
    const remaining = Math.max(500, deadline - Date.now());

    return await new Promise<string>((resolve) => {
      let settled = false;
      let widgetId: string | null = null;
      const finish = (token: string) => {
        if (settled) return;
        settled = true;
        if (widgetId) {
          try {
            api.remove(widgetId);
          } catch {
            /* ignore */
          }
        }
        resolve(token);
      };

      const timer = setTimeout(() => finish(""), remaining);

      try {
        widgetId = api.render(container(), {
          sitekey: TURNSTILE_SITE_KEY,
          execution: "execute",
          appearance: "interaction-only",
          callback: (token: string) => {
            clearTimeout(timer);
            finish(token);
          },
          "error-callback": () => {
            clearTimeout(timer);
            finish("");
          },
          "timeout-callback": () => {
            clearTimeout(timer);
            finish("");
          },
        });
        api.execute(widgetId, {});
      } catch {
        clearTimeout(timer);
        finish("");
      }
    });
  } catch {
    return "";
  }
}
